<template>
  <div class="grid  xl:grid-cols-2 gap-6 w-full font-sans select-none text-left min-h-screen p-6 transition-colors duration-200"
    :class="isDark ? 'bg-black text-zinc-300' : 'bg-zinc-50 text-zinc-800'">
    
    <div class="rounded-xl border p-6 flex flex-col justify-between transition-all duration-200"
      :class="isDark 
        ? 'bg-zinc-900 border-zinc-800 shadow-xl' 
        : 'bg-white border-zinc-200 shadow-sm'">
      <div>
        <div class="flex justify-between items-center mb-6">
          <div class="space-y-1">
            <h2 class="text-sm font-semibold tracking-tight transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-zinc-900'">Flux de Trésorerie</h2>
            <p class="text-xs transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-zinc-500'">Analyse brute des volumes d'encaissements cumulés.</p>
          </div>
          <div class="relative inline-block text-left">
            <select v-model="viewType" class="border rounded-lg pl-3 pr-8 py-1.5 text-xs font-semibold focus:outline-none cursor-pointer appearance-none font-mono transition-all duration-200"
              :class="isDark 
                ? 'bg-zinc-950 border-zinc-700 text-zinc-200 focus:border-zinc-500' 
                : 'bg-white border-zinc-300 text-zinc-900 focus:border-zinc-500 shadow-sm'">
              <option value="month">Mois</option>
              <option value="week">Semaine</option>
            </select>
            <span class="absolute inset-y-0 right-2.5 flex items-center pointer-events-none transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-zinc-600'">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" /></svg>
            </span>
          </div>
        </div>
        <div class="w-full min-h-[300px]">
          <VueApexCharts type="area" height="300" :options="chartOptions" :series="seriesToShow" />
        </div>
      </div>
    </div>

    <div class="rounded-xl border p-6 flex flex-col justify-between transition-all duration-200"
      :class="isDark 
        ? 'bg-zinc-900 border-zinc-800 shadow-xl' 
        : 'bg-white border-zinc-200 shadow-sm'">
      <div>
        <div class="mb-6 space-y-1">
          <h2 class="text-sm font-semibold tracking-tight transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-900'">Performance Actes</h2>
          <p class="text-xs transition-colors duration-200"
            :class="isDark ? 'text-zinc-400' : 'text-zinc-500'">Distribution de la demande par typologie de prestations.</p>
        </div>
        <div class="w-full min-h-[300px]">
          <VueApexCharts type="bar" height="300" :options="agencyChartOptions" :series="seriesAgencyToShow" />
        </div>
      </div>
    </div>

    <div class="border rounded-xl p-6 flex flex-col justify-between transition-all duration-200"
      :class="isDark 
        ? 'bg-zinc-900 border-zinc-800 shadow-xl' 
        : 'bg-white border-zinc-200 shadow-sm'">
      <div class="w-full h-full flex flex-col">
        <div class="mb-6 space-y-1">
          <h2 class="text-sm font-semibold tracking-tight flex items-center gap-2 transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-900'">
            <span class="w-2 h-2 rounded-full inline-block animate-pulse bg-amber-500"></span>
            Alertes & Points de friction
          </h2>
          <p class="text-xs transition-colors duration-200"
            :class="isDark ? 'text-zinc-400' : 'text-zinc-500'">Anomalies opérationnelles requérant une intervention managériale.</p>
        </div>

        <div class="overflow-x-auto no-scrollbar flex-grow">
          <table class="w-full text-xs text-left border-collapse">
            <thead class="text-[11px] font-mono border-b transition-colors duration-200"
              :class="isDark ? 'text-zinc-400 border-zinc-700' : 'text-zinc-600 border-zinc-300'">
              <tr>
                <th class="pb-3 font-bold uppercase tracking-wider">Type</th>
                <th class="pb-3 font-bold uppercase tracking-wider">Structure</th>
                <th class="pb-3 font-bold uppercase tracking-wider">Incident</th>
                <th class="pb-3 text-right font-bold uppercase tracking-wider">Arbitrage</th>
              </tr>
            </thead>
            <tbody class="divide-y transition-colors duration-200"
              :class="isDark ? 'divide-zinc-800' : 'divide-zinc-200'">
              <tr v-for="alert in alerts" :key="alert.id" class="group transition-colors duration-150"
                :class="isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-zinc-50'">
                <td class="py-3.5 pr-2 whitespace-nowrap">
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold font-mono uppercase inline-block border transition-colors duration-200"
                    :class="{
                      'bg-red-950 text-red-400 border-red-800/60': isDark && alert.type === 'danger',
                      'bg-red-100 text-red-900 border-red-300': !isDark && alert.type === 'danger',
                      'bg-amber-950 text-amber-400 border-amber-800/60': isDark && alert.type === 'warning',
                      'bg-amber-100 text-amber-900 border-amber-300': !isDark && alert.type === 'warning',
                      'bg-zinc-800 text-zinc-300 border-zinc-700': isDark && alert.type === 'info',
                      'bg-zinc-200 text-zinc-900 border-zinc-300': !isDark && alert.type === 'info'
                    }">
                    {{ alert.title }}
                  </span>
                </td>
                <td class="py-3.5 font-semibold pr-2 whitespace-nowrap transition-colors duration-200"
                  :class="isDark ? 'text-zinc-200' : 'text-zinc-900'">Salon Principal</td>
                <td class="py-3.5 pr-2 max-w-[200px] truncate transition-colors duration-200 font-normal"
                  :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">{{ alert.message }}</td>
                <td class="py-3.5 text-right whitespace-nowrap">
                  <button class="font-bold text-xs px-3 py-1 border rounded-md shadow-sm transition-all duration-200"
                    :class="isDark 
                      ? 'bg-zinc-800 border-zinc-700 text-amber-400 hover:text-white hover:border-zinc-500' 
                      : 'bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-100 hover:border-zinc-400'">
                    Traiter
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="alerts.length === 0" class="flex flex-col items-center justify-center py-16 space-y-2 flex-grow">
          <svg class="w-5 h-5" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-xs font-medium" :class="isDark ? 'text-zinc-500' : 'text-zinc-500'">Infrastructure saine. Aucune anomalie détectée.</p>
        </div>
      </div>
    </div>

    <div class="rounded-xl border p-6 flex flex-col justify-between transition-all duration-200"
      :class="isDark 
        ? 'bg-zinc-900 border-zinc-800 shadow-xl' 
        : 'bg-white border-zinc-200 shadow-sm'">
      <div>
        <div class="mb-6 space-y-1">
          <h2 class="text-sm font-semibold tracking-tight transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-900'">Taux d'Occupation des Créneaux</h2>
          <p class="text-xs transition-colors duration-200"
            :class="isDark ? 'text-zinc-400' : 'text-zinc-500'">Pourcentage d'heures ouvrables occupées par des rendez-vous confirmés sur les 30 derniers jours.</p>
        </div>
        <div class="w-full min-h-[300px]">
          <VueApexCharts type="area" height="300" :options="devisChartOptions" :series="seriesDevisToShow" />
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import VueApexCharts from "vue3-apexcharts"
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

const { isDark } = useTheme();
const viewType = ref("month")

const alerts = ref([])

const caData = ref([])
const categories = ref([])
const seriesReal = ref([{ name: "Volume d'affaires", data: [] }])

const agencyData = ref([])
const agencyCategories = ref([])
const seriesAgency = ref([{ name: "Prestations", data: [] }])

const devisData = ref([])
const devisCategories = ref([])
const seriesDevis = ref([{ name: "Rendez-vous", data: [] }])

/* =======================
   DYNAMIC THEME CHARTS PARAMETERS (CONTRAST ENHANCED)
======================= */
const dynamicColors = computed(() => ({
  grid: isDark.value ? "#27272a" : "#e4e4e7",          // Lignes de grille bien distinctes
  labels: isDark.value ? "#a1a1aa" : "#4b5563",        // Textes des axes lisibles à coup sûr
  primaryLine: isDark.value ? "#fbbf24" : "#111827",   // Or vif en sombre / Noir profond en clair
  secondaryLine: isDark.value ? "#38bdf8" : "#2563eb", // Bleu électrique / Bleu roi
  barColor: isDark.value ? "#3f3f46" : "#4b5563",      // Barres de graphe sombres bien visibles
  tooltipTheme: isDark.value ? "dark" : "light"
}))

/* =======================
   COMPUTED OPTIONS CHARTS
======================= */
const chartOptions = computed(() => ({
  chart: { 
    toolbar: { show: false }, 
    fontFamily: "inherit",
    background: "transparent", // Évite le carré blanc en mode sombre
    noData: {
      text: 'Aucune donnée disponible',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: isDark.value ? '#a1a1aa' : '#71717a',
        fontSize: '12px'
      }
    }
  },
  theme: {
    mode: isDark.value ? "dark" : "light" // Aligne automatiquement la palette interne d'ApexCharts
  },
  stroke: { curve: "smooth", width: 2.5, colors: [dynamicColors.value.primaryLine] },
  xaxis: {
    axisBorder: { show: true, color: dynamicColors.value.grid },
    axisTicks: { show: true, color: dynamicColors.value.grid },
    labels: { style: { colors: dynamicColors.value.labels, fontSize: "11px", fontWeight: 500 } },
    categories: categories.value
  },
  yaxis: {
    labels: { 
      style: { colors: dynamicColors.value.labels, fontSize: "11px", fontWeight: 500 },
      formatter: v => `${v.toLocaleString("fr-FR")} €`
    }
  },
  colors: [dynamicColors.value.primaryLine],
  dataLabels: { enabled: false },
  fill: {
    type: "gradient",
    gradient: { shadeOpacity: 0, opacityFrom: isDark.value ? 0.25 : 0.15, opacityTo: 0, stops: [0, 90] },
  },
  grid: { borderColor: dynamicColors.value.grid, strokeDashArray: 2 },
  tooltip: { theme: dynamicColors.value.tooltipTheme }
}))

const agencyChartOptions = computed(() => ({
  chart: { 
    toolbar: { show: false },
    background: "transparent",
    noData: {
      text: 'Aucune donnée disponible',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: isDark.value ? '#a1a1aa' : '#71717a',
        fontSize: '12px'
      }
    }
  },
  theme: {
    mode: isDark.value ? "dark" : "light"
  },
  plotOptions: { bar: { borderRadius: 4, columnWidth: "30%" } },
  xaxis: {
    axisBorder: { show: true, color: dynamicColors.value.grid },
    axisTicks: { show: true, color: dynamicColors.value.grid },
    labels: { style: { colors: dynamicColors.value.labels, fontSize: "11px", fontWeight: 500 } },
    categories: agencyCategories.value
  },
  yaxis: { labels: { style: { colors: dynamicColors.value.labels, fontSize: "11px", fontWeight: 500 } } },
  colors: [dynamicColors.value.barColor],
  grid: { borderColor: dynamicColors.value.grid, strokeDashArray: 2 },
  dataLabels: { enabled: false },
  tooltip: { theme: dynamicColors.value.tooltipTheme }
}))

const devisChartOptions = computed(() => ({
  chart: {
    toolbar: { show: false },
    background: "transparent",
    noData: {
      text: 'Aucune donnée disponible',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: isDark.value ? '#a1a1aa' : '#71717a',
        fontSize: '12px'
      }
    }
  },
  theme: {
    mode: isDark.value ? "dark" : "light"
  },
  stroke: { curve: "smooth", width: 2.5, colors: [dynamicColors.value.secondaryLine] },
  xaxis: {
    axisBorder: { show: true, color: dynamicColors.value.grid },
    axisTicks: { show: true, color: dynamicColors.value.grid },
    labels: { style: { colors: dynamicColors.value.labels, fontSize: "11px", fontWeight: 500 } },
    categories: devisCategories.value
  },
  yaxis: {
    labels: {
      style: { colors: dynamicColors.value.labels, fontSize: "11px", fontWeight: 500 },
      formatter: v => `${v}%`
    },
    min: 0,
    max: 100
  },
  colors: [dynamicColors.value.secondaryLine],
  dataLabels: { enabled: false },
  grid: { borderColor: dynamicColors.value.grid, strokeDashArray: 2 },
  fill: {
    type: "gradient",
    gradient: { shadeOpacity: 0, opacityFrom: isDark.value ? 0.20 : 0.15, opacityTo: 0, stops: [0, 95] },
  },
  tooltip: { theme: dynamicColors.value.tooltipTheme }
}))

const seriesToShow = computed(() => seriesReal.value);
const seriesAgencyToShow = computed(() => seriesAgency.value);
const seriesDevisToShow = computed(() => seriesDevis.value);
/* =======================
   DATA MANAGEMENT LIFECYCLE
======================= */
async function loadStaticData() {
  try {
    // 1. Charger le flux de trésorerie (CA)
    const revenueResponse = await api.analytics.getRevenueFlow({ period: viewType.value });
    if (revenueResponse.data && revenueResponse.data.labels && revenueResponse.data.data) {
      categories.value = revenueResponse.data.labels;
      caData.value = revenueResponse.data.data;
      seriesReal.value = [{ name: "Volume d'affaires", data: caData.value }];
    } else {
      categories.value = [];
      caData.value = [];
      seriesReal.value = [{ name: "Volume d'affaires", data: [] }];
    }

    // 2. Charger la performance des services
    const servicesResponse = await api.analytics.getServicesPerformance({ period: viewType.value });
    if (servicesResponse.data && servicesResponse.data.labels && servicesResponse.data.data) {
      agencyCategories.value = servicesResponse.data.labels;
      agencyData.value = servicesResponse.data.data;
      seriesAgency.value = [{ name: "Volume d'actes", data: agencyData.value }];
    } else {
      agencyCategories.value = [];
      agencyData.value = [];
      seriesAgency.value = [{ name: "Volume d'actes", data: [] }];
    }

    // 3. Charger le taux d'occupation (30 derniers jours)
    const responseRateResponse = await api.analytics.getResponseRateHistory();
    const last30Days = responseRateResponse.data || [];

    if (last30Days.length > 0) {
      if (viewType.value === 'week') {
        const last7Days = last30Days.slice(-7);
        devisCategories.value = last7Days.map(d => new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short' }));
        devisData.value = last7Days.map(d => d.rate);
      } else {
        // Afficher les 30 jours mais avec moins de labels pour la lisibilité
        const sampledData = last30Days.filter((_, index) => index % 3 === 0);
        devisCategories.value = sampledData.map(d => {
          const date = new Date(d.date);
          return `${date.getDate()}/${date.getMonth() + 1}`;
        });
        devisData.value = sampledData.map(d => d.rate);
      }
      seriesDevis.value = [{ name: "Occupation (%)", data: devisData.value }];
    } else {
      devisCategories.value = [];
      devisData.value = [];
      seriesDevis.value = [{ name: "Occupation (%)", data: [] }];
    }

    // 4. Charger les alertes
    const alertsResponse = await api.analytics.getAlerts();
    alerts.value = alertsResponse.data || [];

  } catch (error) {
    console.error('Erreur lors du chargement des données analytics:', error);
    // En cas d'erreur, initialiser avec des tableaux vides
    categories.value = [];
    caData.value = [];
    seriesReal.value = [{ name: "Volume d'affaires", data: [] }];

    agencyCategories.value = [];
    agencyData.value = [];
    seriesAgency.value = [{ name: "Volume d'actes", data: [] }];

    devisCategories.value = [];
    devisData.value = [];
    seriesDevis.value = [{ name: "Occupation (%)", data: [] }];

    alerts.value = [];
  }
}

watch(viewType, loadStaticData)
onMounted(loadStaticData)
</script>
<style scoped>
/* Tooltip Custom Overrides dynamisés */
:deep(.apexcharts-theme-dark) {
  background: #09090b !important;
  border: 1px solid #18181b !important;
}
:deep(.apexcharts-theme-light) {
  background: #ffffff !important;
  border: 1px solid #f4f4f5 !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important;
}
:deep(.apexcharts-tooltip-title) {
  font-family: ui-monospace, monospace !important;
  font-size: 10px !important;
}
:deep(.apexcharts-theme-dark .apexcharts-tooltip-title) {
  background: #18181b !important;
  color: #a1a1aa !important;
}
:deep(.apexcharts-theme-light .apexcharts-tooltip-title) {
  background: #fafafa !important;
  color: #71717a !important;
}

.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>